import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import { DELETE_CLIENT_INCOME, EDIT_CLIENT_INCOME } from "../../constants";
import { CardLayout, DeleteModal, MTable } from "../v3";
import { OfficeIncomeTableColumns } from "../../pages/office/constants";
import { FinanceService } from "../../services/finance.service";
import { get_date } from "../generalComponets";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

const DEFAULT_SORT = [{ field: "date", sort: "desc" as const }];

export const OfficeIncome = () => {
  const [incomes, setIncomes] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "office-income",
    defaults: { sortModel: DEFAULT_SORT },
  });
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, recordId: null });

  const canEditIncome = useHasPermission(EDIT_CLIENT_INCOME);
  const canDeleteIncome = useHasPermission(DELETE_CLIENT_INCOME);

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const fetchData = useCallback(async (incomeFilters = {}) => {
    setLoading(true);

    try {
      const incomeResponse = await FinanceService.getAllIncome({ ...incomeFilters, isOffice: true });
      setIncomes(incomeResponse?.data?.rows || []);
      setTotalCount(incomeResponse?.data?.count || 0);
    } catch (error) {
      toast.error("Failed to fetch office income statement");
      console.error("OfficeIncome ~ fetchData error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!dateRange) return;

    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      description: searchText,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
    });
  }, [fetchData, sortModel, paginationState, searchText, dateRange]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(OfficeIncomeTableColumns());
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Office Income" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Office Income",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = (params) => {
    // TODO: Wire office income receipt route when backend support is available.
    navigate(`/client/receipt/${params.row.id}`);
  };

  const openDeleteModal = (income) => {
    setDeleteModal({ isOpen: true, recordId: income.id });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, recordId: null });
  };

  const handleDelete = async () => {
    try {
      await FinanceService.deleteIncome(deleteModal.recordId);
      toast.success("Income deleted successfully");
      fetchData({
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
        description: searchText,
        fromDate: dateRange?.startDate,
        toDate: dateRange?.endDate,
      });
    } catch (error) {
      toast.error("Failed to delete income");
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = (incomeData, actionType) => {
    switch (actionType) {
      case "edit":
        navigate(`/edit-income/${incomeData.id}?accountType=office&tab=income`);
        break;
      case "delete":
        openDeleteModal(incomeData);
        break;
      default:
        return null;
    }
  };

  const columnHeaders = OfficeIncomeTableColumns(handleActions, canEditIncome, canDeleteIncome);

  const rows = incomes.map((income, index) => ({
    sl: index + 1 + paginationState.page * paginationState.pageSize,
    id: income.id,
    date: get_date(income.date),
    description: income.description,
    amount: income.amount,
  }));

  return (
    <CardLayout>
      <MTable
        placeholder="Search Income"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        showSearchBar
        showDateRangePicker
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        showExportButton
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        columnHeaders={columnHeaders}
        tableRows={rows}
        rowCount={totalCount || 0}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        defaultSortModel={DEFAULT_SORT}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this income?"
      />
    </CardLayout>
  );
};
